import {
  Button,
  Divider,
  Input,
  Space,
  Switch,
  type FormInstance,
  type FormItemProps,
  type InputProps,
} from "antd";
import type { TextAreaProps } from "antd/es/input";
import React, { useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { formatEntityNameLabel } from "../../../lib/formats/common";
import type { SaveMutationResult } from "../../../lib/graphql";
import { filterFalse } from "../../../lib/utils";
import { DateTimeFormInput } from "../../finance/DocumentDateFormInput";
import { Cascader } from "../../form/Cascader";
import { DatePicker, type DatePickerProps } from "../../form/DatePicker";
import { Form, type FormProps } from "../../form/Form";
import { InputNumber, type InputNumberProps } from "../../form/InputNumber";
import { SelectField, type SelectFieldProps } from "../../form/SelectField";
import { SubmitButton } from "../../form/SubmitButton";
import { HelpTooltip } from "../HelpTooltip";
import { SidebarFooter } from "../Sidebar/Footer";

export interface ItemFieldFormConfig<TFormValues>
  extends
    Pick<
      SelectFieldProps,
      | "loading"
      | "mode"
      | "showGroups"
      | "onSearch"
      | "onFocus"
      | "options"
      | "newItemProps"
      | "className"
      | "placeholder"
      | "optionsHook"
      | "optionsHookParams"
      | "optionsHookFilter"
      | "formatOption"
      | "labelRenderer"
    >,
    Pick<FormItemProps, "rules"> {
  name?: Extract<keyof TFormValues, string>;
  key?: string | number | null;
  label?: React.ReactNode;
  type:
    | "string"
    | "select"
    | "cascader"
    | "number"
    | "date"
    | "datetime"
    | "text"
    | "switch"
    | "password"
    | "custom";
  render?({
    bag,
    setBag,
    form,
  }: {
    bag: Record<string, any>;
    setBag: (bag: Record<string, any>) => void;
    form: FormInstance;
  }): React.ReactNode;
  defaultValue?: any;
  prefix?: React.ReactNode;
  hidden?: boolean;
  disabled?: boolean;
  formItemProps?: Omit<FormItemProps, "children">;
  optional?: boolean;
  onChange?: (
    value: any,
    item: TFormValues,
    options: any[],
    form: FormInstance
  ) => void;
  numberProps?: InputNumberProps;
  inputProps?: Omit<InputProps & TextAreaProps & SelectFieldProps, "name">;
  dateProps?: DatePickerProps;
  helpTooltipTitle?: ReactNode;
}

export interface ItemFormProps<T, TFormValues> {
  fields: Array<ItemFieldFormConfig<TFormValues> | false | null>;
  item?: T;
  initialValues: TFormValues;
  onCancel?: () => void;
  onSave(
    values: TFormValues,
    form: FormInstance,
    setSubmitting: (submitting: boolean) => void
  ): Promise<SaveMutationResult<T> | null | undefined>;
  formProps?: FormProps<TFormValues>;
  initBag?: (item?: T) => Record<string, any>;
  saveText?: ReactNode;
  clearOnAction?: boolean;
  hideFooter?: boolean;
  entityName?: ReactNode;
  footerLeft?: ReactNode;
}

export function ItemForm<T, TFormValues>(props: ItemFormProps<T, TFormValues>) {
  const [internalForm] = Form.useForm();
  const form = props.formProps?.form || internalForm;
  const values = props.initialValues;
  const [bag, setBag] = useState(
    (props.initBag && props.initBag(props.item)) || {}
  );

  const onCancel = () => {
    if (props.onCancel) props.onCancel();
    if (props.clearOnAction) form.resetFields();
  };

  return (
    <Form
      initialValues={values}
      layout="vertical"
      form={form}
      {...props.formProps}
      onSubmit={(values, { setSubmitting, form, showErrors }) => {
        props
          .onSave(values, form, setSubmitting)
          .then((result) => {
            if (!result) return;

            setSubmitting(false);

            if (!result.result) {
              showErrors(result.errors);
            } else if (props.clearOnAction) {
              form.resetFields();
            }
          })
          .catch(() => setSubmitting(false));
      }}
    >
      {filterFalse(props.fields)
        .filter((f) => !f.hidden)
        .map((field: ItemFieldFormConfig<TFormValues>, index) => {
          const fieldKey = field.key ?? field.name ?? index;

          if (!field.name && field.render) {
            return (
              <React.Fragment key={fieldKey}>
                {field.render({ bag, setBag, form })}
              </React.Fragment>
            );
          }

          let input: ReactNode | null = null;
          switch (field.type) {
            case "string":
              input = (
                <Input
                  name={field.name}
                  prefix={field.prefix}
                  disabled={field.disabled}
                  {...field.inputProps}
                />
              );
              break;
            case "text":
              input = (
                <Input.TextArea
                  name={field.name}
                  rows={4}
                  {...field.inputProps}
                />
              );
              break;
            case "number":
              input = (
                <InputNumber
                  name={field.name}
                  min={0}
                  style={{ width: "100%" }}
                  disabled={field.disabled}
                  onChange={(val) =>
                    field.onChange && field.onChange(val, values, [], form)
                  }
                  {...field.numberProps}
                />
              );
              break;
            case "date":
              input = (
                <DatePicker
                  name={field.name}
                  style={{ width: "100%" }}
                  disabled={field.disabled}
                  {...field.dateProps}
                />
              );
              break;
            case "datetime":
              input = (
                <DateTimeFormInput
                  name={field.name}
                  disabled={field.disabled}
                  dateProps={field.dateProps}
                />
              );
              break;
            case "switch":
              input = <Switch disabled={field.disabled} />;
              break;
            case "password":
              input = <Input.Password disabled={field.disabled} />;
              break;
            case "select": {
              const { allowClear, ...selectInputProps } =
                field.inputProps || {};

              input = (
                <SelectField
                  loading={field.loading}
                  mode={field.mode}
                  onSearch={field.onSearch}
                  onChange={(value, options) =>
                    field.onChange &&
                    field.onChange(value, values, options, form)
                  }
                  onFocus={field.onFocus}
                  options={field.options}
                  showGroups={field.showGroups}
                  placeholder={field.placeholder}
                  defaultValues={field.defaultValue}
                  newItemProps={field.newItemProps}
                  className={field.className}
                  disabled={field.disabled}
                  optionsHook={field.optionsHook}
                  optionsHookParams={field.optionsHookParams}
                  optionsHookFilter={field.optionsHookFilter}
                  formatOption={field.formatOption}
                  labelRenderer={field.labelRenderer}
                  allowClear={allowClear == null ? true : !!allowClear}
                  {...selectInputProps}
                />
              );
              break;
            }
            case "cascader":
              input = (
                <Cascader
                  options={field.options}
                  defaultValue={field.defaultValue}
                  disabled={field.disabled}
                />
              );
              break;
            case "custom":
              input = field.render ? field.render({ bag, setBag, form }) : null;
              break;
          }

          let label = field.label;

          if (!label && field.name === "name")
            label = formatEntityNameLabel(props.entityName);

          const itemName =
            field.type === "custom" && !React.isValidElement(input)
              ? undefined
              : field.name;

          const item = input && (
            <Form.Item
              key={fieldKey}
              name={itemName}
              label={label}
              rules={itemName ? field.rules : undefined}
              valuePropName={
                itemName && field.type === "switch" ? "checked" : undefined
              }
              {...field.formItemProps}
            >
              {input}
            </Form.Item>
          );

          if (field.helpTooltipTitle) {
            return (
              <HelpTooltip key={fieldKey} title={field.helpTooltipTitle}>
                {item}
              </HelpTooltip>
            );
          }

          return item;
        })}

      {!props.hideFooter && (
        <>
          <div style={{ height: "46px" }} />
          <SidebarFooter>
            <Space>
              {props.footerLeft}
              <Divider orientation="vertical" />
              <Button onClick={onCancel}>
                <FormattedMessage id="cancel" defaultMessage="cancel" />
              </Button>
              <SubmitButton type="primary">
                <span>
                  {props.saveText || (
                    <FormattedMessage id="save" defaultMessage="save" />
                  )}
                </span>
              </SubmitButton>
            </Space>
          </SidebarFooter>
        </>
      )}
    </Form>
  );
}
